// client/src/components/wholesaler/TierUsageCard.js
// Subscription Tier Usage Display Card — Compact version

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { wholesalerAPI } from '../../services/api';

const TierUsageCard = () => {
  const { t } = useTranslation(['wholesaler', 'common']);
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTierData();
  }, []);

  const fetchTierData = async () => {
    try {
      const [profileRes, productsRes] = await Promise.all([
        wholesalerAPI.getProfile(),
        wholesalerAPI.getProducts()
      ]);

      const wholesaler = profileRes.data.wholesaler;
      const products = productsRes.data.products || productsRes.data || [];
      const productCount = Array.isArray(products) ? products.length : 0;

      const limits = { 
        starter: 1500, 
        professional: 5000, 
        enterprise: -1 
      };

      setTierData({
        tier: wholesaler.subscriptionTier || 'starter',
        currentProducts: productCount,
        productLimit: limits[wholesaler.subscriptionTier] || 1500,
        ordersThisMonth: wholesaler.monthlyOrderCount || 0
      });
    } catch (err) {
      console.error('Failed to fetch tier data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-2 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  if (!tierData) return null;

  const { tier, currentProducts, productLimit, ordersThisMonth } = tierData;
  const isUnlimited = productLimit === -1;
  const percentage = isUnlimited ? 0 : Math.round((currentProducts / productLimit) * 100);
  
  const getTierDisplayName = (tier) => {
    const names = {
      starter: t('subscription.tiers.starter', 'Starter'),
      professional: t('subscription.tiers.professional', 'Professional'),
      enterprise: t('subscription.tiers.enterprise', 'Enterprise')
    };
    return names[tier] || tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  const getTierBadgeColor = (tier) => {
    const colors = {
      starter: 'bg-blue-100 text-blue-800 border-blue-300',
      professional: 'bg-purple-100 text-purple-800 border-purple-300',
      enterprise: 'bg-amber-100 text-amber-800 border-amber-300'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getProgressBarColor = () => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 90) return 'bg-yellow-500';
    if (percentage >= 75) return 'bg-blue-500';
    return 'bg-green-500';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      {/* Header with Badge */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800">
          {t('subscription.subscriptionPlan', 'Subscription Plan')}
        </h3>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTierBadgeColor(tier)}`}>
          {getTierDisplayName(tier)}
        </span>
      </div>

      {/* Product Slots — compact */}
      <div className="mb-3">
        {isUnlimited ? (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{t('subscription.productSlots', 'Product Slots')}</span>
            <span className="text-sm font-bold text-gray-800">{currentProducts} / &#8734;</span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-500">{t('subscription.productSlots', 'Product Slots')}</span>
              <span className="text-xs text-gray-500">{percentage}% {t('subscription.used', 'used')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                ></div>
              </div>
              <span className="text-sm font-bold text-gray-800 whitespace-nowrap">
                {currentProducts}<span className="text-gray-400 font-normal">/{productLimit}</span>
              </span>
            </div>
          </>
        )}
      </div>

      {/* Orders This Month — compact row */}
      <div className="flex items-center justify-between py-2 border-t border-gray-100">
        <span className="text-xs text-gray-500">
          {t('subscription.ordersThisMonth', 'Orders This Month')}
        </span>
        <span className="text-sm font-bold text-gray-800">{ordersThisMonth}</span>
      </div>

      {/* Warning */}
      {!isUnlimited && percentage >= 90 && (
        <div className={`mt-2 p-2 rounded-lg text-xs ${percentage >= 100 ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-yellow-50 border border-yellow-200 text-yellow-700'}`}>
          {percentage >= 100 
            ? t('subscription.limitReachedShort', "You've reached your product limit")
            : t('subscription.runningLow', 'Running low on product slots')
          }
        </div>
      )}

      {/* View Plans */}
      <Link
        to="/wholesaler/pricing"
        className="mt-3 w-full block text-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium"
      >
        {t('subscription.viewAllPlans', 'View All Plans')}
      </Link>
    </div>
  );
};

export default TierUsageCard;